/**
 * Axis-aligned box movement against a voxel grid.
 *
 * The standard approach for block games: move one axis at a time and snap the
 * leading face flush against whatever it hits. Resolving axes separately is
 * what lets you slide along a wall instead of stopping dead against it.
 *
 * Pure maths over a `SolidTest` callback - no world, no engine, no renderer.
 */

/** Is the voxel at these integer coordinates solid? */
export type SolidTest = (x: number, y: number, z: number) => boolean;

/**
 * How much of a cell is solid, measured up from its floor: 0 to 1.
 *
 * A block used to be solid or not, and everything solid filled its whole
 * cube whatever it looked like. That is why сходи lift you a full block when
 * they draw half of one, why a плита would leave you standing half a block
 * in the air, and why a тин could never be taller than the cell it sits in.
 *
 * A *fraction* of a cell answers all three. A плита fills a half. A тин
 * fills its own cell and half of the one above it, which is what makes one
 * course of it higher than a jump. And everything that was solid before
 * fills one, which is why passing nothing here leaves the world behaving
 * exactly as it did.
 */
export type FillTest = (x: number, y: number, z: number) => number;

/**
 * Gap left between the box and any surface it rests on. Without it, a box
 * sitting exactly at y=5.0 counts as inside the block below and jitters.
 */
const EPS = 1e-4;

/**
 * No single resolution step may move further than this, or a fast-moving box
 * could pass clean through a block between frames. Anything larger is split.
 */
const MAX_SUBSTEP = 0.4;

export interface MoveResult {
  x: number;
  y: number;
  z: number;
  /** True when movement along that axis was cut short by a block. */
  hitX: boolean;
  hitY: boolean;
  hitZ: boolean;
  /** True when the box is resting on something. */
  onGround: boolean;
  /** True when the box hit its head. Used to cancel upward velocity. */
  hitCeiling: boolean;
}

/** Box bounds as [min, max] per axis, indexed 0=x 1=y 2=z. */
type Bounds = [number, number, number];

/**
 * Clamp movement along one axis so the box stops flush against solid voxels.
 * Returns the delta actually allowed.
 *
 * Only the leading face's cell layer needs testing: the box was not
 * overlapping anything before this call, and substepping guarantees it cannot
 * cross more than one voxel boundary here.
 */
function sweepAxis(
  min: Bounds,
  max: Bounds,
  axis: 0 | 1 | 2,
  delta: number,
  fillAt: FillTest,
): number {
  if (delta === 0) return 0;

  const b = ((axis + 1) % 3) as 0 | 1 | 2;
  const c = ((axis + 2) % 3) as 0 | 1 | 2;

  // Where the leading face lands, and which voxel layer that is.
  const target = (delta > 0 ? max[axis] : min[axis]) + delta;
  const layer = Math.floor(delta > 0 ? target - EPS : target);

  // Every voxel column the box spans on the other two axes.
  const b0 = Math.floor(min[b] + EPS);
  const b1 = Math.floor(max[b] - EPS);
  const c0 = Math.floor(min[c] + EPS);
  const c1 = Math.floor(max[c] - EPS);

  const p: [number, number, number] = [0, 0, 0];
  p[axis] = layer;

  /*
   * Falling onto something is the one case where how full a cell is matters
   * to *where* you stop rather than only to whether you stop.
   *
   * Everything else snaps against a cell boundary: a face you walk into is
   * at the near edge of its cell, and a ceiling is at the bottom of its own.
   * A floor is not - the top of a плита is halfway up the cell it is in, and
   * that is the number the feet have to land on.
   */
  if (axis === 1 && delta < 0) {
    let highest = -Infinity;
    for (let bi = b0; bi <= b1; bi++) {
      p[b] = bi;
      for (let ci = c0; ci <= c1; ci++) {
        p[c] = ci;
        const fill = fillAt(p[0], p[1], p[2]);
        if (fill <= 0) continue;
        const top = layer + fill;
        // Only a surface the feet are coming down onto: one they are already
        // below is a cell they are inside, and stopping on it would push
        // them up through it.
        if (top <= min[1] + EPS && top > highest) highest = top;
      }
    }
    if (highest > -Infinity && highest > target) return highest + EPS - min[1];
    return delta;
  }

  for (let bi = b0; bi <= b1; bi++) {
    p[b] = bi;
    for (let ci = c0; ci <= c1; ci++) {
      p[c] = ci;
      const fill = fillAt(p[0], p[1], p[2]);
      if (fill <= 0) continue;
      /*
       * Sideways, a part-filled cell only blocks what is level with the part
       * that is there. Walk at a плита with your feet on top of it and there
       * is nothing in the way; walk at it from the floor and there is.
       */
      if (axis !== 1 && min[1] >= p[1] + fill - EPS) continue;
      // Snap flush against the blocking voxel's near face.
      return delta > 0
        ? layer - EPS - max[axis]
        : layer + 1 + EPS - min[axis];
    }
  }
  return delta;
}

/**
 * Does a box overlap one specific voxel cell?
 *
 * Used to refuse placing a block inside the player - without it you can seal
 * yourself into the ground.
 */
export function boxOverlapsVoxel(
  x: number, y: number, z: number,
  halfWidth: number, height: number,
  bx: number, by: number, bz: number,
): boolean {
  return (
    x - halfWidth < bx + 1 && x + halfWidth > bx &&
    y < by + 1 && y + height > by &&
    z - halfWidth < bz + 1 && z + halfWidth > bz
  );
}

/** Would the box at this position overlap anything solid? */
function overlaps(
  x: number, y: number, z: number,
  halfWidth: number, height: number,
  fillAt: FillTest,
): boolean {
  const x0 = Math.floor(x - halfWidth + EPS);
  const x1 = Math.floor(x + halfWidth - EPS);
  const y0 = Math.floor(y + EPS);
  const y1 = Math.floor(y + height - EPS);
  const z0 = Math.floor(z - halfWidth + EPS);
  const z1 = Math.floor(z + halfWidth - EPS);

  for (let yi = y0; yi <= y1; yi++) {
    for (let zi = z0; zi <= z1; zi++) {
      for (let xi = x0; xi <= x1; xi++) {
        const fill = fillAt(xi, yi, zi);
        // The box runs from y to y+height; the cell's stuff from yi to
        // yi+fill. They overlap or they do not.
        if (fill > 0 && y < yi + fill - EPS && y + height > yi + EPS) {
          return true;
        }
      }
    }
  }
  return false;
}

/** One collision-resolved pass. Delta must already be substepped. */
function resolveOnce(
  x: number, y: number, z: number,
  halfWidth: number, height: number,
  dx: number, dy: number, dz: number,
  fillAt: FillTest,
): MoveResult {
  const min: Bounds = [x - halfWidth, y, z - halfWidth];
  const max: Bounds = [x + halfWidth, y + height, z + halfWidth];

  // Vertical first, so `onGround` is settled before horizontal movement.
  const ady = sweepAxis(min, max, 1, dy, fillAt);
  min[1] += ady; max[1] += ady;
  const hitY = ady !== dy;

  const adx = sweepAxis(min, max, 0, dx, fillAt);
  min[0] += adx; max[0] += adx;

  const adz = sweepAxis(min, max, 2, dz, fillAt);
  min[2] += adz; max[2] += adz;

  return {
    x: min[0] + halfWidth,
    y: min[1],
    z: min[2] + halfWidth,
    hitX: adx !== dx,
    hitY,
    hitZ: adz !== dz,
    onGround: hitY && dy < 0,
    hitCeiling: hitY && dy > 0,
  };
}

/**
 * Move a box through the voxel world, resolving collisions.
 *
 * `stepHeight` lets the box walk up a low ledge instead of stopping against
 * it - without this, every one-block rise in the terrain is a wall and
 * walking anywhere is miserable.
 *
 * `onGround` is whether the box was standing on something when the caller
 * handed it over, and it is what says whether the step is allowed. Stepping
 * is a walking move: you get a foot up on a ledge because the other one is
 * on the floor. In the air there is nothing to push off.
 */
export function moveAABB(
  x: number, y: number, z: number,
  halfWidth: number, height: number,
  dx: number, dy: number, dz: number,
  isSolid: SolidTest,
  stepHeight = 0,
  onGround = false,
  /**
   * How full each cell is, where the caller knows about shapes.
   *
   * Optional, and what it defaults to is the whole of why this could be
   * added at all: a world that answers only "solid or not" gets every solid
   * cell filled to the brim, which is what every one of them was before.
   * Nothing that does not pass this changes behaviour by a hair.
   */
  fillAt?: FillTest,
): MoveResult {
  const fill: FillTest = fillAt ??
    ((x2, y2, z2) => (isSolid(x2, y2, z2) ? 1 : 0));
  const distance = Math.abs(dx) + Math.abs(dy) + Math.abs(dz);
  const steps = Math.max(1, Math.ceil(distance / MAX_SUBSTEP));

  const sx = dx / steps;
  const sy = dy / steps;
  const sz = dz / steps;

  let cur: MoveResult = {
    x, y, z,
    hitX: false, hitY: false, hitZ: false,
    onGround: false, hitCeiling: false,
  };

  for (let i = 0; i < steps; i++) {
    const startX = cur.x;
    const startZ = cur.z;
    /*
     * Standing on something, either when we arrived or by the last substep.
     *
     * It said `cur.onGround || i === 0`, and that `i === 0` handed out a free
     * step on the first substep of *every* call - in the air as readily as on
     * the floor. A player jumps 1.25 blocks and steps 1.05, so pressed
     * against a wall in mid-flight they got 2.3 and stood on top of a
     * two-block wall: "гравець стрибає на 2 блоки вгору а не на 1". The
     * comment on STEP_HEIGHT had been claiming a two-block wall stops you
     * either way for as long as it had not.
     *
     * The caller knows whether its feet were down; it was simply never
     * asked.
     */
    const wasOnGround = cur.onGround || (i === 0 && onGround);

    const next = resolveOnce(cur.x, cur.y, cur.z, halfWidth, height, sx, sy, sz, fill);

    // Blocked horizontally while on the ground? Try again from a step up.
    if (stepHeight > 0 && wasOnGround && (next.hitX || next.hitZ)) {
      const stepped = tryStep(
        startX, cur.y, startZ, halfWidth, height,
        sx, sz, stepHeight, fill,
      );
      if (stepped !== null) {
        const gainedNormally = Math.abs(next.x - startX) + Math.abs(next.z - startZ);
        const gainedStepped = Math.abs(stepped.x - startX) + Math.abs(stepped.z - startZ);
        if (gainedStepped > gainedNormally + EPS) {
          cur = { ...stepped, hitY: next.hitY, hitCeiling: next.hitCeiling };
          continue;
        }
      }
    }

    // Sticky ground: once landed, stay landed for the rest of the substeps.
    cur = { ...next, onGround: next.onGround || (wasOnGround && sy <= 0 && next.hitY) };
  }

  return cur;
}

/**
 * Attempt to climb a ledge: rise by `stepHeight`, move horizontally, then
 * settle back down. Returns null if there is no room to do it.
 */
function tryStep(
  x: number, y: number, z: number,
  halfWidth: number, height: number,
  dx: number, dz: number,
  stepHeight: number,
  fillAt: FillTest,
): MoveResult | null {
  // Is there headroom to rise at all?
  const raisedY = y + stepHeight;
  if (overlaps(x, raisedY, z, halfWidth, height, fillAt)) return null;

  const across = resolveOnce(x, raisedY, z, halfWidth, height, dx, 0, dz, fillAt);
  if (across.hitX && across.hitZ) return null; // still walled in up there

  // Settle back onto whatever is underneath.
  const down = resolveOnce(
    across.x, across.y, across.z, halfWidth, height,
    0, -stepHeight, 0, fillAt,
  );
  // Only a real step if we actually landed on something.
  if (!down.hitY) return null;

  return { ...down, onGround: true };
}
